import { supabase } from '../api/supabase';

interface VehicleAssignment {
  id: string;
  vehicle_id: string;
  start_date: string;
  end_date: string | null;
  daily_rate: number;
  status: string;
  vehicles?: {
    license_plate: string;
    make_model: string;
    daily_rental_rate: number;
    daily_rental_rate_incl_vat: number;
    daily_rental_rate_new: number;
  };
}

interface AssignmentHistory {
  id: string;
  vehicle_id: string;
  assignment_date: string;
  return_date: string | null;
  daily_rate: number;
  total_days: number;
  total_amount: number;
  status: string;
  vehicles?: {
    license_plate: string;
    make_model: string;
    daily_rental_rate: number;
    daily_rental_rate_incl_vat: number;
    daily_rental_rate_new: number;
  };
}

interface RentDetail {
  vehicleId: string;
  licensePlate: string;
  makeModel: string;
  startDate: string;
  endDate: string;
  days: number;
  dailyRate: number;
  totalAmount: number;
  status: 'active' | 'completed';
  source: 'current' | 'history';
}

/**
 * Get comprehensive rent breakdown for a driver using actual assignment data
 * Combines current assignments and historical records
 */
export async function getDriverRentDetails(driverId: string, period?: { startDate?: string; endDate?: string }) {
  try {
    console.log('🔍 Getting rent details for driver:', driverId);

    // Get all assignments from assignment_history
    const { data: historyAssignments, error: historyError } = await supabase
      .from('assignment_history')
      .select(`
        id,
        vehicle_id,
        assignment_date,
        return_date,
        daily_rate,
        total_days,
        total_amount,
        status,
        vehicles(
          license_plate,
          make_model,
          daily_rental_rate,
          daily_rental_rate_incl_vat,
          daily_rental_rate_new
        )
      `)
      .eq('primary_driver_id', driverId)
      .order('assignment_date', { ascending: true });

    if (historyError) {
      console.warn('Warning getting assignment history:', historyError.message);
    }

    console.log('📊 Found assignments:', {
      total: historyAssignments?.length || 0
    });

    // Process all assignments
    const currentRentDetails: RentDetail[] = (historyAssignments || []).map(assignment => {
      const startDate = new Date(assignment.assignment_date);
      const endDate = assignment.return_date ? new Date(assignment.return_date) : new Date();
      
      // Apply period filter if specified
      let periodStartDate = startDate;
      let periodEndDate = endDate;
      
      if (period?.startDate) {
        const filterStart = new Date(period.startDate);
        periodStartDate = startDate > filterStart ? startDate : filterStart;
      }
      
      if (period?.endDate) {
        const filterEnd = new Date(period.endDate);
        periodEndDate = endDate < filterEnd ? endDate : filterEnd;
      }

      // Skip if no overlap with period
      if (periodStartDate > periodEndDate) {
        return null;
      }

      // Calculate days (inclusive of both start and end date)
      const diffTime = Math.abs(periodEndDate.getTime() - periodStartDate.getTime());
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      // Determine daily rate (priority: assignment rate > vehicle new rate > vehicle incl VAT rate > vehicle base rate > default)
      const dailyRate = assignment.daily_rate_new || 
                      assignment.daily_rate || 
                      assignment.vehicles?.daily_rental_rate_new ||
                      assignment.vehicles?.daily_rental_rate_incl_vat ||
                      assignment.vehicles?.daily_rental_rate ||
                      95; // Default fallback

      return {
        vehicleId: assignment.vehicle_id,
        licensePlate: assignment.vehicles?.license_plate || 'Unknown',
        makeModel: assignment.vehicles?.make_model || 'Unknown Vehicle',
        startDate: periodStartDate.toISOString().split('T')[0],
        endDate: periodEndDate.toISOString().split('T')[0],
        days,
        dailyRate,
        totalAmount: days * dailyRate,
        status: assignment.end_date ? 'completed' : 'active',
        source: 'current'
      } as RentDetail;
    }).filter((detail): detail is RentDetail => detail !== null);

    // Process historical assignments
    const historyRentDetails: RentDetail[] = (historyAssignments || []).map(assignment => {
      const startDate = new Date(assignment.assignment_date);
      const endDate = assignment.return_date ? new Date(assignment.return_date) : new Date();
      
      // Apply period filter if specified
      let periodStartDate = startDate;
      let periodEndDate = endDate;
      
      if (period?.startDate) {
        const filterStart = new Date(period.startDate);
        periodStartDate = startDate > filterStart ? startDate : filterStart;
      }
      
      if (period?.endDate) {
        const filterEnd = new Date(period.endDate);
        periodEndDate = endDate < filterEnd ? endDate : filterEnd;
      }

      // Skip if no overlap with period
      if (periodStartDate > periodEndDate) {
        return null;
      }

      // Use stored values if available, otherwise calculate
      const days = assignment.total_days || (Math.ceil(Math.abs(periodEndDate.getTime() - periodStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);
      const dailyRate = assignment.daily_rate || 
                      assignment.vehicles?.daily_rental_rate_new ||
                      assignment.vehicles?.daily_rental_rate_incl_vat ||
                      assignment.vehicles?.daily_rental_rate ||
                      95;
      const totalAmount = assignment.total_amount || (days * dailyRate);

      return {
        vehicleId: assignment.vehicle_id,
        licensePlate: assignment.vehicles?.license_plate || 'Unknown',
        makeModel: assignment.vehicles?.make_model || 'Unknown Vehicle',
        startDate: periodStartDate.toISOString().split('T')[0],
        endDate: periodEndDate.toISOString().split('T')[0],
        days,
        dailyRate,
        totalAmount,
        status: assignment.return_date ? 'completed' : 'active',
        source: 'history'
      } as RentDetail;
    }).filter((detail): detail is RentDetail => detail !== null);

    // Combine all rent details and remove duplicates (prefer current over history)
    const allRentDetails = [...currentRentDetails];
    
    // Add history details that don't overlap with current assignments
    historyRentDetails.forEach(historyDetail => {
      const hasOverlap = currentRentDetails.some(currentDetail => 
        currentDetail.vehicleId === historyDetail.vehicleId &&
        currentDetail.startDate <= historyDetail.endDate &&
        currentDetail.endDate >= historyDetail.startDate
      );
      
      if (!hasOverlap) {
        allRentDetails.push(historyDetail);
      }
    });

    // Sort by start date
    allRentDetails.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    // Calculate summary
    const totalDays = allRentDetails.reduce((sum, detail) => sum + detail.days, 0);
    const totalAmount = allRentDetails.reduce((sum, detail) => sum + detail.totalAmount, 0);
    const uniqueVehicles = new Set(allRentDetails.map(detail => detail.vehicleId)).size;
    const activeAssignments = allRentDetails.filter(detail => detail.status === 'active').length;

    console.log('📈 Rent calculation summary:', {
      totalDays,
      totalAmount,
      uniqueVehicles,
      activeAssignments,
      totalAssignments: allRentDetails.length
    });

    return {
      rentDetails: allRentDetails,
      summary: {
        totalDays,
        totalAmount,
        uniqueVehicles,
        activeAssignments,
        completedAssignments: allRentDetails.length - activeAssignments,
        averageDailyRate: totalDays > 0 ? totalAmount / totalDays : 0,
        period: period ? `${period.startDate} to ${period.endDate}` : 'All time'
      }
    };

  } catch (error) {
    console.error('❌ Error getting driver rent details:', error);
    throw error;
  }
}

/**
 * Get rent summary grouped by vehicle for easier analysis
 */
export async function getDriverRentSummaryByVehicle(driverId: string, period?: { startDate?: string; endDate?: string }) {
  const { rentDetails, summary } = await getDriverRentDetails(driverId, period);
  
  // Group by vehicle
  const vehicleSummary = rentDetails.reduce((acc, detail) => {
    const key = detail.vehicleId;
    if (!acc[key]) {
      acc[key] = {
        vehicleId: detail.vehicleId,
        licensePlate: detail.licensePlate,
        makeModel: detail.makeModel,
        totalDays: 0,
        totalAmount: 0,
        assignments: [],
        status: 'completed'
      };
    }
    
    acc[key].totalDays += detail.days;
    acc[key].totalAmount += detail.totalAmount;
    acc[key].assignments.push({
      startDate: detail.startDate,
      endDate: detail.endDate,
      days: detail.days,
      dailyRate: detail.dailyRate,
      amount: detail.totalAmount,
      source: detail.source
    });
    
    // If any assignment is active, mark vehicle as active
    if (detail.status === 'active') {
      acc[key].status = 'active';
    }
    
    return acc;
  }, {} as Record<string, any>);

  return {
    summary,
    vehicles: Object.values(vehicleSummary),
    totalVehicles: Object.keys(vehicleSummary).length
  };
}
